import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, FileSpreadsheet, FileText, Tags, Trash2, Check, MessagesSquare } from 'lucide-react';
import InteractionDialog from '@/components/InteractionDialog';
import { InteractionCard, useInteractionLookups } from '@/components/InteractionCard';
import { downloadFile } from '@/lib/download';
import { KINDS, COUNTERPARTS } from '@/lib/interactions';

const API = `${window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || ''}/api`;
const ALL = 'all';
const LIMIT = 20;

const TopicsDialog = ({ open, onOpenChange, topics, onChanged }) => {
  const [name, setName] = useState('');
  const [edits, setEdits] = useState({});
  const run = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'No se pudo completar'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="topics-dialog">
        <DialogHeader>
          <DialogTitle>Temas</DialogTitle>
          <DialogDescription>Etiquetas para clasificar las entradas. Un tema en uso no se puede borrar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {topics.map((t) => (
            <div key={t.id} className="flex items-center gap-2">
              {t.id === 'general' ? (
                <span className="flex-1 text-sm px-3 py-2 text-slate-500">General (fijo)</span>
              ) : (
                <>
                  <Input className="flex-1" value={edits[t.id] ?? t.name} onChange={(e) => setEdits((s) => ({ ...s, [t.id]: e.target.value }))} />
                  {edits[t.id] !== undefined && edits[t.id] !== t.name && (
                    <Button size="icon" variant="ghost" title="Guardar" onClick={() => run(async () => { await axios.put(`${API}/interactions/topics/${t.id}`, { name: edits[t.id] }); setEdits((s) => { const n = { ...s }; delete n[t.id]; return n; }); }, 'Tema actualizado')}><Check className="w-4 h-4 text-green-600" /></Button>
                  )}
                  <Button size="icon" variant="ghost" title="Borrar" onClick={() => run(() => axios.delete(`${API}/interactions/topics/${t.id}`), 'Tema eliminado')}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t">
          <Input placeholder="Nuevo tema" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-topic-input" />
          <Button onClick={() => run(async () => { await axios.post(`${API}/interactions/topics`, { name }); setName(''); }, 'Tema creado')} data-testid="new-topic-add">Agregar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Interactions = () => {
  usePageTitle('Reuniones y notas');
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const admin = isAdmin || isSuperAdmin;
  const { topics, clubs, reload } = useInteractionLookups();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [scouts, setScouts] = useState([]);
  const [filters, setFilters] = useState({ search: '', kind: ALL, counterpart_type: ALL, topic_id: ALL, club: ALL, scout_id: ALL, date_from: '', date_to: '' });
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState({ open: false, entry: null });
  const [topicsOpen, setTopicsOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => setFilters((f) => ({ ...f, search })), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => {
    if (!admin) return;
    axios.get(`${API}/auth/users`).then((r) => setScouts(r.data.filter((u) => ['scout', 'admin', 'superadmin'].includes(u.role)))).catch(() => {});
  }, [admin]);

  const params = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/interactions`, { params: { ...params(), page, limit: LIMIT } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination);
    } catch (_) { toast.error('No se pudieron cargar las entradas'); }
    setLoading(false);
  }, [params, page]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); reload(); };
  const sel = (key, label, options) => (
    <Select value={filters[key]} onValueChange={(v) => setFilters((f) => ({ ...f, [key]: v }))}>
      <SelectTrigger className="w-[170px] h-9 text-sm" data-testid={`filter-${key}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <Layout>
      <div className="space-y-4" data-testid="interactions-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><MessagesSquare className="w-7 h-7" /> Reuniones y notas</h1>
            <p className="text-sm text-slate-600" data-testid="interactions-total">
              {pagination.total} entrada(s){admin ? ' de todos los scouts' : ' tuyas'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {admin && <Button variant="outline" onClick={() => setTopicsOpen(true)} data-testid="manage-topics"><Tags className="w-4 h-4 mr-2" />Temas</Button>}
            <Button variant="outline" onClick={() => downloadFile(`${API}/interactions/export`, { params: { ...params(), format: 'pdf' }, fallbackName: 'reuniones.pdf' })} data-testid="interactions-pdf"><FileText className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" onClick={() => downloadFile(`${API}/interactions/export`, { params: { ...params(), format: 'xlsx' }, fallbackName: 'reuniones.xlsx' })} data-testid="interactions-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
            <Button className="bg-brand-600 hover:bg-brand-700" onClick={() => setDialog({ open: true, entry: null })} data-testid="new-interaction"><Plus className="w-4 h-4 mr-2" />Nueva entrada</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Input className="w-[220px] h-9" placeholder="Buscar en las notas" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="filter-search" />
          {sel('kind', 'Todos los tipos', KINDS)}
          {sel('counterpart_type', 'Con cualquiera', COUNTERPARTS)}
          {sel('topic_id', 'Todos los temas', topics.map((t) => ({ value: t.id, label: t.name })))}
          {sel('club', 'Todos los clubes', clubs.map((c) => ({ value: c, label: c })))}
          {admin && sel('scout_id', 'Todos los scouts', scouts.map((s) => ({ value: s.id, label: s.full_name || s.username })))}
          <div className="flex items-center gap-1"><Label className="text-xs">Desde</Label><Input type="date" className="h-9 w-[140px]" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} /></div>
          <div className="flex items-center gap-1"><Label className="text-xs">Hasta</Label><Input type="date" className="h-9 w-[140px]" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} /></div>
        </div>

        <div className="space-y-3">
          {loading ? <p className="text-slate-500">Cargando…</p> : rows.length === 0 ? (
            <p className="text-slate-500 py-8 text-center" data-testid="interactions-empty">No hay entradas con estos filtros. Con "Nueva entrada" registrás una reunión o charla.</p>
          ) : rows.map((e) => (
            <InteractionCard key={e.id} entry={e} showScout={admin} canEdit={admin || e.created_by === user?.id}
              onEdit={(entry) => setDialog({ open: true, entry })} onDeleted={refresh} />
          ))}
        </div>

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
            <span className="text-sm">Página {page} de {pagination.total_pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.total_pages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
          </div>
        )}
      </div>
      <InteractionDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} entry={dialog.entry} topics={topics} clubs={clubs} onSaved={refresh} />
      {admin && <TopicsDialog open={topicsOpen} onOpenChange={setTopicsOpen} topics={topics} onChanged={reload} />}
    </Layout>
  );
};

export default Interactions;
